// WorkflowContext.tsx
import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import axios from 'axios';
import { WorkflowDetails } from '../../axiosApi/models/workflow-details';
import Api from '../../axiosApi/api';
import { useApi } from '../../api/ApiProvider';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import intl from 'components/i18n/ReactIntlWrapper';
import { handleAPIError } from 'common/errorHandler';
import useEffectOnce from 'components/Hooks/useEffectOnce';

interface WorkflowContextProps {
  workflow: WorkflowDetails | null;
  loading: boolean;
  errors: Error | null;
}



const WorkflowContext = createContext<WorkflowContextProps | undefined>(undefined);

interface WorkflowProviderProps {
  moduleId: number;
  children: ReactNode;
}

// Cache to store fetched workflows
const workflowCache = new Map<number, WorkflowDetails>();
// Track ongoing requests to prevent multiple calls for the same moduleId
const ongoingRequests = new Set<number>();

export const WorkflowProvider: React.FC<WorkflowProviderProps> = ({ moduleId, children }) => {
  const [workflow, setWorkflow] = useState<WorkflowDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [errors, setErrors] = useState<Error | null>(null);

  const api: Api = useApi();
  const toast = useToastMessageQueue();

  useEffect(() => {
    if (moduleId) {
      // Check if workflow is already cached
      const cachedWorkflow = workflowCache.get(moduleId);
      if (cachedWorkflow) {
        setWorkflow(cachedWorkflow);
        setLoading(false);
        return;
      }

      // Check if there's already an ongoing request for this moduleId
      if (ongoingRequests.has(moduleId)) {
        // Wait for the ongoing request by checking periodically
        const checkInterval = setInterval(() => {
          const updatedCachedWorkflow = workflowCache.get(moduleId);
          if (updatedCachedWorkflow) {
            setWorkflow(updatedCachedWorkflow);
            setLoading(false);
            clearInterval(checkInterval);
          }
        }, 100);
        return () => clearInterval(checkInterval);
      }

      // Mark this moduleId as having an ongoing request
      ongoingRequests.add(moduleId);
      setLoading(true);

      api.workflowApi.apiVversionWorkflowModuleIdActiveScopedGet(moduleId,"1" ,{}).then((response)=>{
        if (response.data?.data) {
          setWorkflow(response.data.data);
          // Cache the workflow data
          workflowCache.set(moduleId, response.data.data);
        } else {
          setWorkflow(null);
        }
        setLoading(false);
        // Remove from ongoing requests
        ongoingRequests.delete(moduleId);
      }).catch((error)=>{
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
        setLoading(false);
        // Remove from ongoing requests even on error
        ongoingRequests.delete(moduleId);
      });
    };
  }, [moduleId]);

  return (
    <WorkflowContext.Provider value={{ workflow, loading, errors }}>
      {children}
    </WorkflowContext.Provider>
  );
};

export const useWorkflow = (): WorkflowContextProps => {
  const context = useContext(WorkflowContext);
  if (context === undefined) {
    throw new Error('useWorkflow must be used within a WorkflowProvider');
  }
  return context;
};
